import React from 'react';
import { Link } from 'react-router-dom';
import {
  Phone,
  Mail,
  MapPin,
  MessageCircle,
  Heart,
  ArrowUp,
} from 'lucide-react';

const Footer = ({ lang = 'en' }) => {
  const isAr = lang === 'ar';
  const currentYear = new Date().getFullYear();

  const content = {
    description: isAr
      ? 'نحن ملتزمون بتقديم خدمات رعاية صحية منزلية متميزة في الكويت، مع التركيز على الراحة والأمان والاحترافية.'
      : 'Dedicated to providing premium home healthcare services in Kuwait, focusing on comfort, safety, and professional excellence.',
    quickLinks: {
      title: isAr ? 'روابط سريعة' : 'Quick Links',
      links: [
        { name: isAr ? 'الرئيسية' : 'Home', path: '/' },
        { name: isAr ? 'خدماتنا' : 'Services', path: '/services' },
        { name: isAr ? 'الأسعار' : 'Pricing', path: '/pricing' },
        { name: isAr ? 'اتصل بنا' : 'Contact', path: '/contact' },
      ],
    },
    services: {
      title: isAr ? 'خدماتنا' : 'Our Services',
      items: [
        isAr ? 'رعاية كبار السن' : 'Elderly Care',
        isAr ? 'رعاية الأم والطفل' : 'Mother & Baby Care',
        isAr ? 'التمريض المنزلي' : 'Home Nursing',
        isAr ? 'رعاية ذوي الاحتياجات' : 'Special Needs Care',
      ],
    },
    contact: {
      title: isAr ? 'تواصل معنا' : 'Contact Info',
      phone: '+965 50097296',
      email: 'info@rightchoicekw.com',
      address: isAr
        ? 'مدينة الكويت، الكويحولي، بلوك 7، شارع تونس، مجمع الرحاب'
        : 'Hawally, block 7, Tunisia street, Al rehab complex',
    },
    rights: isAr
      ? `© ${currentYear} رايت تشويس للرعاية الصحية. جميع الحقوق محفوظة.`
      : `© ${currentYear} Right Choice Health Care. All rights reserved.`,
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <footer
      className='bg-medical-blue-900 text-white pt-20 pb-10'
      dir={isAr ? 'rtl' : 'ltr'}
    >
      <div className='section-padding max-w-7xl mx-auto'>
        <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-16'>
          {/* Brand Column */}
          <div className='space-y-6'>
            <Link to='/' className='flex items-center gap-3 group'>
              {/* Circular Logo Wrapper */}
              <div className='h-12 w-12 rounded-full overflow-hidden border-2 border-white/10 bg-white/5 flex items-center justify-center transition-transform group-hover:scale-110'>
                <img
                  src='/images/logo.png'
                  alt='Right Choice'
                  className='h-full w-full object-cover'
                />
              </div>

              <div className='flex flex-col'>
                <span className='text-xl font-black leading-none text-white'>
                  RIGHT CHOICE
                </span>
                <span className='text-[10px] font-bold text-medical-blue-400 uppercase tracking-widest'>
                  Health Care
                </span>
              </div>
            </Link>

            <p className='text-medical-blue-200 text-sm leading-relaxed'>
              {content.description}
            </p>

            <div className='flex gap-4'>
              <a
                href='tel:+96550097296'
                className='p-2 bg-white/10 rounded-lg hover:bg-medical-blue-600 transition-colors text-white'
              >
                <Phone size={18} />
              </a>
              <a
                href='https://wa.me/96550097296'
                className='p-2 bg-white/10 rounded-lg hover:bg-[#25D366] transition-colors text-white'
              >
                <MessageCircle size={18} />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className='text-lg font-bold mb-6 border-b border-white/10 pb-2 inline-block'>
              {content.quickLinks.title}
            </h4>
            <ul className='space-y-4'>
              {content.quickLinks.links.map((link, i) => (
                <li key={i}>
                  <Link
                    to={link.path}
                    className='text-medical-blue-200 hover:text-white transition-colors text-sm flex items-center gap-2'
                  >
                    <span className='w-1.5 h-1.5 rounded-full bg-medical-blue-500' />
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services Column */}
          <div>
            <h4 className='text-lg font-bold mb-6 border-b border-white/10 pb-2 inline-block'>
              {content.services.title}
            </h4>
            <ul className='space-y-4'>
              {content.services.items.map((item, i) => (
                <li key={i} className='text-medical-blue-200 text-sm'>
                  {item}
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Column */}
          <div>
            <h4 className='text-lg font-bold mb-6 border-b border-white/10 pb-2 inline-block'>
              {content.contact.title}
            </h4>
            <ul className='space-y-4'>
              <li className='flex items-start gap-3 text-sm text-medical-blue-200'>
                <Phone size={18} className='text-medical-blue-400 shrink-0' />
                <span>{content.contact.phone}</span>
              </li>
              <li className='flex items-start gap-3 text-sm text-medical-blue-200'>
                <Mail size={18} className='text-medical-blue-400 shrink-0' />
                <span className='break-all'>{content.contact.email}</span>
              </li>
              <li className='flex items-start gap-3 text-sm text-medical-blue-200'>
                <MapPin size={18} className='text-medical-blue-400 shrink-0' />
                <span>{content.contact.address}</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className='pt-8 border-t border-white/10 flex flex-col md:flex-row justify-between items-center gap-6'>
          <p className='text-xs text-medical-blue-300 font-medium'>
            {content.rights}
          </p>
          <div className='flex items-center gap-2 text-xs text-medical-blue-300'>
            {isAr ? 'صنع بكل' : 'Made with'}{' '}
            <Heart size={14} className='text-red-500 fill-red-500' />{' '}
            {isAr ? 'في الكويت' : 'in Kuwait'}
          </div>
          <button
            onClick={scrollToTop}
            aria-label='Scroll to top'
            className='p-3 bg-white/5 hover:bg-white/10 rounded-xl transition-all group'
          >
            <ArrowUp
              size={20}
              className='group-hover:-translate-y-1 transition-transform'
            />
          </button>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
